import { useEffect, useMemo, useState } from "react";
import { FormProvider, Path, useForm, useWatch } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Card,
  CardContent,
  createFilterOptions,
  Grid,
  Typography,
} from "@mui/material";

import {
  Form,
  FormAutocomplete,
  FormStateField,
  FormTextField,
  FormZipcodeField,
  Tooltip,
} from "@covertree/ui";
import { OptionType } from "@covertree/types";
import { FORM_ERROR_DELAY_IN_MS, states } from "@covertree/consts";

import { Lender, lenders, lendersOptions } from "@covertree/features";
import {
  createMortgageBillFormValidationSchema,
  defaultValues,
  MortgageBillFormState,
} from "./form";
import { ConfirmPurchase } from "../../ConfirmPurchase";
import { AdditionalInterestContainer } from "../../../AdditionalInterest";
import { useCheckout } from "../../../CheckoutProvider";

const filter = createFilterOptions<OptionType>();

const fieldsToClear = ["streetAddress", "unit", "city", "state", "zip"];

interface Props {
  forceAgentSignResponsibility?: boolean | null;
  disabled?: boolean;
  isD2C?: boolean;
  onSubmit(formValues: MortgageBillFormState): void;
}

export function MortgageBillPaymentForm({
  forceAgentSignResponsibility,
  disabled,
  onSubmit,
  isD2C = false,
}: Props) {
  const [wasCustomName, setWasCustomName] = useState(false);

  const {
    isAdditionalInterestsEnabled,
    hasLienholderError,
    setLender,
    setLoanNumber,
    setMortgageBillFormValid,
  } = useCheckout();

  const form = useForm({
    defaultValues: {
      ...defaultValues(isAdditionalInterestsEnabled),
      ...(forceAgentSignResponsibility
        ? { agentSignResponsibility: true }
        : {}),
    },
    mode: "all",
    resolver: yupResolver(
      createMortgageBillFormValidationSchema(
        isD2C,
        forceAgentSignResponsibility,
        isAdditionalInterestsEnabled
      )
    ),
    delayError: FORM_ERROR_DELAY_IN_MS,
  });

  const {
    handleSubmit: submit,
    control,
    formState: { isValid },
    watch,
    setValue,
    trigger,
  } = form;

  const lenderName = watch("lender.name");
  const lenderDetails = useWatch({
    name: [
      "lender.name",
      "lender.streetAddress",
      "lender.unit",
      "lender.city",
      "lender.state",
      "lender.zip",
      "lender.email",
    ],
    control,
  });
  const loanNumber = useWatch({ name: "loanNumber", control });

  const mortgageTooltip = useMemo(() => {
    if (isAdditionalInterestsEnabled) {
      return "Only enter a mortgage company here if they are responsible for paying the insurance premium. If they just need to be notified about the policy — such as a community manager, landlord, or park owner — please add them under the “Notify interested parties” section instead.";
    }

    return "Some lenders require you to pay your insurance premium as part of you monthly mortgage payment where they keep it in your escrow account. They then write a check to the insurer out of this account. If your lender requires this you should select Mortgage Bill.";
  }, [isAdditionalInterestsEnabled]);

  useEffect(() => {
    if (lenderName) {
      const lenderDetails = Object.entries(lenders[lenderName.value] ?? {});

      const areFieldsPredefined = !!lenderDetails.length;

      if (!areFieldsPredefined && !wasCustomName)
        fieldsToClear.forEach((field) =>
          setValue(`lender.${field}` as Path<MortgageBillFormState>, "")
        );

      lenderDetails.forEach(([field, value]) => {
        if (field !== "name" && field !== "state")
          setValue(
            `lender.${field}` as Path<MortgageBillFormState>,
            value ?? "",
            {
              shouldValidate: true,
            }
          );

        if (field === "state") {
          const foundState = states.find(
            (stateOption) => value === stateOption.value
          );
          setValue(
            `lender.${field}` as Path<MortgageBillFormState>,
            foundState,
            {
              shouldValidate: true,
            }
          );
        }
      });

      setWasCustomName(!areFieldsPredefined);
    }
  }, [lenderName, setValue, wasCustomName, trigger]);

  useEffect(() => {
    const [lenderName, streetAddress, unit, city, state, zip, email] =
      lenderDetails;
    if (
      lenderDetails &&
      !!lenderName?.value &&
      !!streetAddress &&
      !!city &&
      !!state?.value &&
      !!zip
    ) {
      const lender: Lender = {
        name: lenderName.value,
        streetAddress,
        unit,
        city,
        state: state.value,
        zip,
        email: email as string,
      };
      setLender(lender);
    } else {
      setLender(undefined);
    }
  }, [lenderDetails]);

  useEffect(() => {
    setLoanNumber(loanNumber);
  }, [loanNumber]);

  useEffect(() => {
    setMortgageBillFormValid(isValid);
  }, [isValid, setMortgageBillFormValid]);

  return (
    <FormProvider {...form}>
      <Form onSubmit={submit(onSubmit)}>
        <Card
          sx={{
            overflow: "visible",
          }}>
          <CardContent>
            <Typography gutterBottom>
              Mortgage Bill{" "}
              <Tooltip
                dataTestId="mortgage-bill-tooltip"
                tooltip={mortgageTooltip}
              />
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormAutocomplete
                  id="lender-name"
                  label="Lender Name"
                  options={lendersOptions}
                  name="lender.name"
                  control={control}
                  disabled={disabled}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    if (params.inputValue.trim() !== "") {
                      filtered.push({
                        value: params.inputValue,
                        label: `Add "${params.inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  selectOnFocus
                  clearOnBlur
                  getOptionLabel={(option) => `${option.value}`}
                  renderOption={(props, option) => (
                    <li {...props}>{option.label}</li>
                  )}
                  placeholder="Start Typing..."
                />
              </Grid>
              <Grid item xs={12}>
                <FormTextField
                  id="loan-number"
                  name="loanNumber"
                  label="Loan Number"
                  control={control}
                  disabled={disabled}
                />
              </Grid>
            </Grid>
            <Box mt={2}>
              <Typography gutterBottom>Lender Address</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormTextField
                  id="lender-street-address"
                  name="lender.streetAddress"
                  label="Street Address"
                  control={control}
                  disabled={disabled}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <FormTextField
                  id="lender-unit"
                  name="lender.unit"
                  label="Unit / Lot #"
                  control={control}
                  disabled={disabled}
                  optional
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <FormTextField
                  id="lender-city"
                  name="lender.city"
                  label="City"
                  control={control}
                  disabled={disabled}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <FormStateField
                  id="lender-state"
                  name="lender.state"
                  label="State"
                  control={control}
                  disabled={disabled}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <FormZipcodeField
                  id="lender-zip"
                  name="lender.zip"
                  label="Zip Code"
                  control={control}
                  disabled={disabled}
                  extendedZipcode
                />
              </Grid>
              {isAdditionalInterestsEnabled ? (
                <Grid item xs={12}>
                  <FormTextField
                    id="lender-email"
                    name="lender.email"
                    label="Email"
                    control={control}
                    disabled={disabled}
                    type="email"
                    tooltip="This email is only used for identification purposes. No communication will be sent."
                    optional
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Typography
                    fontWeight={400}
                    component="span"
                    variant="subtitle2"
                    color="textSecondary">
                    Additional Lenders on the house can be added after placing
                    the order.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {isAdditionalInterestsEnabled && <AdditionalInterestContainer />}

        <ConfirmPurchase disabled={!isValid || hasLienholderError} />
      </Form>
    </FormProvider>
  );
}
